import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { theme } from '../../config/theme';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/authSlice';
import ProfileMenu from './ProfileMenu';
import { MenuIcon, BellIcon } from '../icons/Icons';

const MainLayout = ({ children, title = '' }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  // Close sidebar automatically when switching to desktop
  useEffect(() => {
    if (!isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [isMobile, isSidebarOpen]);

  const containerStyles = {
    minHeight: '100vh',
    backgroundColor: theme.colors.gray[50],
    position: 'relative',
  };

  const logoStyles = {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
  };

  const headerStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '64px',
    backgroundColor: theme.colors.white,
    zIndex: theme.zIndex.navbar,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `0 ${theme.spacing.lg}`,
    boxShadow: theme.shadows.sm,
    marginLeft: 0,
    transition: theme.transitions.base,
  };

  const mainStyles = {
    marginLeft: isMobile ? '0px' : '250px',
    minHeight: 'calc(100vh - 64px)',
    marginTop: '64px',
    transition: theme.transitions.base,
  };

  const overlayStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: theme.zIndex.dropdown - 2, // Just below the sidebar
    display: isMobile && isSidebarOpen ? 'block' : 'none',
    opacity: isMobile && isSidebarOpen ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out',
  };

  return (
    <div style={containerStyles}>
      <Sidebar
        isMobile={isMobile}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Mobile Backdrop Overlay */}
      <div
        style={overlayStyles}
        onClick={() => setIsSidebarOpen(false)}
        aria-hidden="true"
      />

      <header style={headerStyles}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
          {isMobile && (
            <button 
              onClick={() => setIsSidebarOpen(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: theme.spacing.xs, display: 'flex' }}
            >
              <MenuIcon size={24} color={theme.colors.text.primary} />
            </button>
          )}
          {!isMobile && (
             <div style={{...logoStyles, width: 'calc(250px - 24px)'}} onClick={() => navigate('/')}>
               <img 
                 src="/images/logo.png" 
                 alt="AlarcTrade Logo" 
                 style={{ 
                   height: '32px', // Adjust size as needed
                   width: 'auto', 
                   objectFit: 'contain' 
                 }} 
               />
               <span style={{
                 fontSize: theme.typography.fontSize.xl,
                 fontWeight: theme.typography.fontWeight.bold,
                 background: `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%)`,
                 WebkitBackgroundClip: 'text',
                 WebkitTextFillColor: 'transparent',
                 letterSpacing: '-0.5px'
               }}>
                 AlarcTrade
               </span>
             </div>
          )}
          <h1 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
            {title}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: theme.spacing.xs, display: 'flex' }}>
            <BellIcon size={24} color={theme.colors.text.primary} />
          </button>
          <ProfileMenu user={user} isMobile={isMobile} />
        </div>
      </header>

      <main style={mainStyles}>
        {children || <Outlet />}
      </main>
    </div>
  );
};

export default MainLayout;